import type { QueryResultSet } from '../../../../types/queryExecution';
import { writeClipboardRich, writeClipboardText } from '../../../../lib/clipboard';
import { resultSetToInsertStatements, type InsertIdentityMode } from './insertStatementBuilder';
import { columnHeaderLabel } from './plaintextResultsUtils';

/**
 * Escapes a CSV field value according to RFC 4180.
 * - Wraps in quotes if value contains comma, newline, or quote
 * - Doubles internal quotes
 */
function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  const strValue = typeof value === 'object' ? JSON.stringify(value) : String(value);

  // If value contains comma, newline, or quote, wrap in quotes
  if (strValue.includes(',') || strValue.includes('\n') || strValue.includes('\r') || strValue.includes('"')) {
    return `"${strValue.replace(/"/g, '""')}"`;
  }

  return strValue;
}

/**
 * Converts a result set to CSV string format for clipboard copying.
 */
export function resultSetToCsv(resultSet: QueryResultSet): string {
  const columns = resultSet.columns ?? [];
  const rows = resultSet.rows ?? [];

  if (columns.length === 0) {
    return '';
  }

  const lines: string[] = [];

  // Header row
  const headerLine = columns.map((col, index) => escapeCsvField(columnHeaderLabel(col, index))).join(',');
  lines.push(headerLine);

  // Data rows
  for (const row of rows) {
    const rowLine = columns.map((col) => escapeCsvField(row[col.id ?? col.name])).join(',');
    lines.push(rowLine);
  }

  return lines.join('\n');
}

/**
 * Converts table explorer data (rows + columns) to CSV string format.
 */
export function tableDataToCsv(
  rows: Record<string, unknown>[],
  columns: { name: string }[],
  selectedColumns: string[] | '*'
): string {
  if (columns.length === 0) {
    return '';
  }

  // Determine which columns to include
  const columnsToExport =
    selectedColumns === '*'
      ? columns
      : selectedColumns
          .map((colName) => columns.find((c) => c.name === colName))
          .filter((c): c is { name: string } => c !== undefined);

  if (columnsToExport.length === 0) {
    return '';
  }

  const lines: string[] = [];

  // Header row
  const headerLine = columnsToExport.map((col) => escapeCsvField(col.name)).join(',');
  lines.push(headerLine);

  // Data rows
  for (const row of rows) {
    const rowLine = columnsToExport.map((col) => escapeCsvField(row[col.name])).join(',');
    lines.push(rowLine);
  }

  return lines.join('\n');
}

/**
 * Joins a column's cell values into a single comma-separated string for the
 * "Copy values" header action. Strings are wrapped in double-quotes (with
 * embedded quotes/newlines escaped) so the output can be pasted into an
 * IN-clause / array literal without further fix-up. Null/undefined become
 * empty entries; objects/arrays are JSON-stringified so JSON columns paste as
 * their raw shape rather than `[object Object]`.
 */
export function columnValuesToCsvList(values: readonly unknown[]): string {
  return values
    .map((v) => {
      if (v === null || v === undefined) return '';
      if (typeof v === 'string') return JSON.stringify(v);
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    })
    .join(', ');
}

/**
 * Escapes a value for Markdown table cells.
 * - Replaces pipes with escaped pipes
 * - Replaces newlines with spaces
 */
function escapeMarkdownCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  const strValue = typeof value === 'object' ? JSON.stringify(value) : String(value);

  // Escape pipes and replace newlines
  return strValue.replace(/\|/g, '\\|').replace(/\n/g, ' ').replace(/\r/g, '');
}

/**
 * Converts a result set to Markdown table format for clipboard copying.
 */
export function resultSetToMarkdown(resultSet: QueryResultSet): string {
  const columns = resultSet.columns ?? [];
  const rows = resultSet.rows ?? [];

  if (columns.length === 0) {
    return '';
  }

  const lines: string[] = [];

  // Header row
  const headerLine =
    '| ' + columns.map((col, index) => escapeMarkdownCell(columnHeaderLabel(col, index))).join(' | ') + ' |';
  lines.push(headerLine);

  // Separator row
  const separatorLine = '| ' + columns.map(() => '---').join(' | ') + ' |';
  lines.push(separatorLine);

  // Data rows
  for (const row of rows) {
    const rowLine = '| ' + columns.map((col) => escapeMarkdownCell(row[col.id ?? col.name])).join(' | ') + ' |';
    lines.push(rowLine);
  }

  return lines.join('\n');
}

/**
 * Converts table explorer data (rows + columns) to Markdown table format.
 */
export function tableDataToMarkdown(
  rows: Record<string, unknown>[],
  columns: { name: string }[],
  selectedColumns: string[] | '*'
): string {
  if (columns.length === 0) {
    return '';
  }

  // Determine which columns to include
  const columnsToExport =
    selectedColumns === '*'
      ? columns
      : selectedColumns
          .map((colName) => columns.find((c) => c.name === colName))
          .filter((c): c is { name: string } => c !== undefined);

  if (columnsToExport.length === 0) {
    return '';
  }

  const lines: string[] = [];

  // Header row
  const headerLine = '| ' + columnsToExport.map((col) => escapeMarkdownCell(col.name)).join(' | ') + ' |';
  lines.push(headerLine);

  // Separator row
  const separatorLine = '| ' + columnsToExport.map(() => '---').join(' | ') + ' |';
  lines.push(separatorLine);

  // Data rows
  for (const row of rows) {
    const rowLine = '| ' + columnsToExport.map((col) => escapeMarkdownCell(row[col.name])).join(' | ') + ' |';
    lines.push(rowLine);
  }

  return lines.join('\n');
}

/**
 * Converts a result set to INSERT statements for clipboard copying.
 * `identityColumns` / `identityMode` are optional: omitting them yields the
 * historical, identity-unaware output verbatim.
 */
export function resultSetToInsert(
  resultSet: QueryResultSet,
  options?: { engineId?: string; identityColumns?: readonly string[]; identityMode?: InsertIdentityMode }
): string {
  return resultSetToInsertStatements(resultSet, {
    engineId: options?.engineId,
    identityColumns: options?.identityColumns,
    identityMode: options?.identityMode,
  });
}

/**
 * Escapes a value for use inside an HTML element. Apps like Teams, Outlook,
 * and Word recognise an HTML <table> on the clipboard and render it as a
 * formatted table when pasted.
 */
export function escapeHtmlCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Converts a result set to an HTML <table> string for rich clipboard copying.
 * Pasting this into Teams, Outlook, Word, or Confluence renders a real table.
 */
export function resultSetToHtml(resultSet: QueryResultSet): string {
  const columns = resultSet.columns ?? [];
  const rows = resultSet.rows ?? [];

  if (columns.length === 0) {
    return '';
  }

  const headerCells = columns.map((col, index) => `<th>${escapeHtmlCell(columnHeaderLabel(col, index))}</th>`).join('');
  const bodyRows = rows
    .map((row) => {
      const cells = columns
        .map((col) => `<td>${escapeHtmlCell(row[col.id ?? col.name])}</td>`)
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  return `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`;
}

/**
 * Converts Table Explorer data (rows + columns + column filter) to an HTML
 * <table> string for rich clipboard copying. Same shape as `tableDataToCsv`.
 */
export function tableDataToHtml(
  rows: Record<string, unknown>[],
  columns: { name: string }[],
  selectedColumns: string[] | '*'
): string {
  if (columns.length === 0) {
    return '';
  }

  const columnsToExport =
    selectedColumns === '*'
      ? columns
      : selectedColumns
          .map((colName) => columns.find((c) => c.name === colName))
          .filter((c): c is { name: string } => c !== undefined);

  if (columnsToExport.length === 0) {
    return '';
  }

  const headerCells = columnsToExport.map((col) => `<th>${escapeHtmlCell(col.name)}</th>`).join('');
  const bodyRows = rows
    .map((row) => {
      const cells = columnsToExport.map((col) => `<td>${escapeHtmlCell(row[col.name])}</td>`).join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  return `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`;
}

/**
 * Writes both an HTML table and a plain-text fallback to the clipboard in a
 * single ClipboardItem. Teams, Outlook, Word, and Confluence read text/html
 * and render the formatted table; Notepad, terminals, and Monaco read
 * text/plain. Falls back to plain-text writeText() if the rich API throws or
 * is unavailable.
 */
export async function writeRichToClipboard(text: string, html: string): Promise<void> {
  // Empty HTML means the caller intentionally wants a plain copy (e.g.
  // single-cell Ctrl+C should paste a raw value, not a 1-cell table).
  if (html === '') {
    await writeClipboardText(text);
    return;
  }
  await writeClipboardRich(text, html);
}
